import React, { useEffect, useState } from 'react';

import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { Alert, Box, Link, Stack } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridFilterOperator,
  GridLogicOperator,
  GridPaginationModel,
  GridSortModel,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';
import { DateRangePicker, LocalizationProvider, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useGetGHAParticipationReport } from '~/hooks/apis/public-offer-feed/useGHAParticipationReport';

import { GHAParticipationReportRow, GHAParticipationSortBy } from '~/services/PublicOfferFeedService';
import type { Dayjs } from '~/services/TimeService';

import ExportCsvButton from './ExportCsvButton';
import PropertyNameFilterInput from './PropertyNameFilterInput';
import ReportToolbar from './ReportToolbar';
import {
  DEFAULT_SORT_MODEL,
  PAGE_SIZE_OPTIONS,
  buildReportSearchParams,
  parseReportSearchParams,
} from './reportSearchParams';
import {
  COUNTRY_OPTIONS,
  CountryOption,
  MAX_RANGE_DAYS,
  getCountryFilter,
  getLatestReportDate,
  getPropertyAdminPath,
  getPropertyNameFilter,
  getUnfilteredFields,
} from './utils';

const countryFilterOperators = getGridSingleSelectOperators().filter((operator) => operator.value === 'isAnyOf');

// Filtered by svc-public-offer-feed; the grid only collects the search.
const propertyNameFilterOperators: Array<GridFilterOperator<GHAParticipationReportRow>> = [
  { label: 'contains', value: 'contains', getApplyFilterFn: () => null, InputComponent: PropertyNameFilterInput },
];

const sharedColumnOpts: Partial<GridColDef<GHAParticipationReportRow>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const countryColumnOpts: Partial<GridColDef<GHAParticipationReportRow>> = {
  ...sharedColumnOpts,
  filterable: true,
  type: 'singleSelect',
  valueOptions: COUNTRY_OPTIONS,
  getOptionValue: (option: CountryOption) => option.code,
  getOptionLabel: (option: CountryOption) => option.label,
  filterOperators: countryFilterOperators,
  flex: 0.8,
  minWidth: 180,
};

const getRowId = (row: GHAParticipationReportRow) => `${row.propertyId}-${row.userCountry}-${row.propertyCountry}`;

const columns: Array<GridColDef<GHAParticipationReportRow>> = [
  {
    ...sharedColumnOpts,
    field: 'propertyId',
    headerName: 'Property ID',
    width: 290,
    sortable: false,
    renderCell: ({ value }) => (
      <Link component={RouterLink} to={getPropertyAdminPath(value)} target="_blank" rel="noopener noreferrer">
        {value}
      </Link>
    ),
  },
  {
    ...sharedColumnOpts,
    field: 'propertyName',
    headerName: 'Property name',
    filterable: true,
    filterOperators: propertyNameFilterOperators,
    flex: 1.2,
    minWidth: 220,
    valueGetter: (value) => value ?? '—',
  },
  {
    ...sharedColumnOpts,
    field: 'opportunities',
    headerName: 'Opportunities',
    type: 'number',
    flex: 0.6,
    minWidth: 130,
    valueFormatter: (value: number) => value.toLocaleString(),
  },
  { ...countryColumnOpts, field: 'userCountry', headerName: 'User location' },
  { ...countryColumnOpts, field: 'propertyCountry', headerName: 'Property location' },
];

export default function GHAParticipationReportContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once on mount; after that local state leads and the effect below writes it back.
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => parseReportSearchParams(searchParams).dateRange);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    () => parseReportSearchParams(searchParams).filterModel,
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    () => parseReportSearchParams(searchParams).paginationModel,
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(() => parseReportSearchParams(searchParams).sortModel);
  const [rangeError, setRangeError] = useState<string | null>(null);

  const [startDate, endDate] = dateRange;
  const sort = sortModel[0] ?? DEFAULT_SORT_MODEL[0];

  const reportQuery = {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    propertyName: getPropertyNameFilter(filterModel),
    userCountries: getCountryFilter(filterModel, 'userCountry'),
    propertyCountries: getCountryFilter(filterModel, 'propertyCountry'),
    sortBy: sort.field as GHAParticipationSortBy,
    sortOrder: sort.sort ?? 'desc',
  };

  const { data, isFetching, error } = useGetGHAParticipationReport({
    ...reportQuery,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
  });

  // Keeps the URL shareable; the grid's half-built filter rows stay in local state until they have a value.
  useEffect(() => {
    const next = buildReportSearchParams({ dateRange, filterModel, sortModel, paginationModel });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [dateRange, filterModel, sortModel, paginationModel, searchParams, setSearchParams]);

  const resetToFirstPage = () => setPaginationModel((model) => ({ ...model, page: 0 }));

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateRangePicker
            value={dateRange}
            onAccept={([start, end]) => {
              if (!start || !end) {
                return;
              }
              // Mirrors the proxy's cap; every day of range is another day of BigQuery scan.
              if (end.diff(start, 'day') >= MAX_RANGE_DAYS) {
                setRangeError(`Pick a range of ${MAX_RANGE_DAYS} days or fewer.`);
                return;
              }
              setRangeError(null);
              setDateRange([start, end]);
              resetToFirstPage();
            }}
            calendars={1}
            closeOnSelect={false}
            format="DD/MM/YYYY"
            maxDate={getLatestReportDate()}
            localeText={{ okButtonLabel: 'Confirm' }}
            slots={{ field: SingleInputDateRangeField }}
            slotProps={{
              textField: {
                label: 'Enter a date range',
                size: 'small',
                error: Boolean(rangeError),
                helperText: rangeError,
                sx: { width: 280 },
              },
              actionBar: { actions: ['cancel', 'accept'] },
            }}
          />
        </LocalizationProvider>
        <ExportCsvButton query={reportQuery} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Couldn't load the Customer Demand Report: {error.message}
        </Alert>
      )}

      <Box mt={2}>
        <DataGridPro
          rows={data?.result ?? []}
          columns={columns}
          getRowId={getRowId}
          loading={isFetching}
          autoHeight
          pagination
          disableRowSelectionOnClick
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          sortingOrder={['desc', 'asc']}
          rowCount={data?.count ?? 0}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model.length ? model : DEFAULT_SORT_MODEL);
            resetToFirstPage();
          }}
          filterModel={filterModel}
          onFilterModelChange={(model) => {
            setFilterModel(model);
            resetToFirstPage();
          }}
          slots={{ toolbar: ReportToolbar, pagination: GridPagination }}
          slotProps={{
            filterPanel: {
              logicOperators: [GridLogicOperator.And],
              // One filter per column: "is any of" already covers several countries, and a second AND filter could only narrow it.
              getColumnForNewFilter: ({ currentFilters }) => getUnfilteredFields({ items: currentFilters })[0] ?? null,
              filterFormProps: {
                filterColumns: ({ field, currentFilters }) => [
                  field,
                  ...getUnfilteredFields({ items: currentFilters }),
                ],
                // AND is the only option, so the disabled "And" select is noise.
                logicOperatorInputProps: { sx: { display: 'none' } },
                valueInputProps: { sx: { width: 420 }, InputComponentProps: { limitTags: 3 } },
              },
            },
          }}
        />
      </Box>
    </>
  );
}
